<template>
  <main class="settings">
    <Header class="settings__header">
      <router-link
        :to="{ name: 'settings' }"
        class="header__action header__action--disabled"
      >
        <IconSettings />
        Configurações
      </router-link>
      <router-link
        id="header-back-to-pages"
        :to="{ name: 'pagelist' }"
        class="header__action"
      >
        Páginas
      </router-link>
      <router-link
        id="header-back-to-modals"
        :to="{ name: 'modallist' }"
        class="header__action"
      >
        Pop-ups
      </router-link>
      <router-link
        :to="{ name: 'metrics' }"
        class="header__action"
      >
        Métricas
      </router-link>
      <router-link
        v-if="isGlobalUser"
        id="header-back-to-admin"
        :to="{ name: 'admin' }"
        class="header__action"
      >
        Admin
      </router-link>
      <a
        href=""
        class="header__action logout-link"
        @click.prevent="logout"
      >
        Sair
      </a>
    </Header>

    <div class="settings__content">
      <component
        :is="currentInnerView.component"
        :ref="currentInnerViewIndex"
        :key="currentInnerViewIndex"
        :groups="currentInnerView.groups"
        @update:editing="isEditing = $event"
      >
        <aside
          slot="menu"
          class="settings-grid__menu custom-scrollbar"
        >
          <h1>Configurações</h1>
          <nav>
            <ul>
              <li
                v-for="(view, index) in innerViews"
                :key="index"
              >
                <router-link
                  :to="getInnerViewLink(index)"
                  class="settings-grid__menu__link"
                >
                  {{ view.label }}
                </router-link>
              </li>
            </ul>
          </nav>
        </aside>
        <h2 slot="title">
          {{ currentInnerView.label }}
        </h2>
      </component>
    </div>

    <Footer />
  </main>
</template>

<script>
import Header from '@/components/Header.vue';
import Footer from '@/components/Footer.vue';
import IconSettings from '@/components/icons/IconSettings.vue';
import { mapGetters } from 'vuex';
import innerViews, { initialView } from './innerViews';

const componentEnter = async (to, next, nextError) => {
  // Get individual innerView component beforeEnter and send to error if fails
  const { beforeEnter } = innerViews[to.hash.slice(1) || initialView];
  if (beforeEnter) {
    try {
      await beforeEnter();
    } catch {
      next(nextError);
    }
  }
};

async function routeChange(to, from, next) {
  const routeNext = () => {
    next();
  };
  // Set response to send to innerView individual beforeLeave
  const responseTo = {
    label: to.meta.label,
    next: routeNext,
  };
  if (to.name === 'settings') {
    // Call individual component enter if it's a route update
    await componentEnter(to, next, { hash: '' });
    if (to.hash) {
      // Define label based on innerView
      responseTo.label = `${to.meta.label} > ${
        innerViews[to.hash.slice(1)].label
      }`;
    }
  }
  if (this.innerViewRef.beforeLeave) {
    // Calls individual component beforeLeave with to label and next function
    this.innerViewRef.beforeLeave(responseTo);
  } else {
    routeNext();
  }
}

const routeEnter = async (to, from, next) => {
  // Creates next callback which receives an error as param and returns the
  // the next callback handling it and setting previous route
  const nextBehavior =
    ({ error }) =>
    () => {
      if (error) {
        next({ hash: '' });
      }
      next();
    };

  // Call individual component enter with nextBeahvior
  // handling error as error callback
  await componentEnter(to, next, nextBehavior({ error: true }));

  // Call successful next with nextBeahvior not handling error
  next(nextBehavior({ error: false }));
};

export default {
  name: 'Settings',
  components: {
    Header,
    Footer,
    IconSettings,
  },
  beforeRouteEnter: routeEnter,
  beforeRouteUpdate: routeChange,
  beforeRouteLeave: routeChange,
  data() {
    return {
      isEditing: false,
      innerViews,
    };
  },
  computed: {
    currentInnerViewIndex() {
      return this.$route.hash.slice(1) || initialView;
    },
    currentInnerView() {
      return this.innerViews[this.currentInnerViewIndex];
    },
    innerViewRef() {
      return this.$refs[this.currentInnerViewIndex];
    },
    ...mapGetters(['isGlobalUser']),
  },
  beforeCreate() {
    Object.entries(innerViews).forEach(([viewName, viewConfig]) => {
      if (
        viewConfig.isEnabled &&
        !viewConfig.isEnabled(this.$store.state.loggedUser) // mapstate não funciona
      ) {
        delete innerViews[viewName];
      }
    });
  },
  methods: {
    getInnerViewLink(view) {
      const hash = view === initialView ? '' : view;
      return { hash };
    },
    logout() {
      localStorage.removeItem('editor-token');
      window.location.href = `${window.location.origin}/login`; // forçar reload completo
    },
  },
};
</script>

<style scoped lang="scss">
.settings {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f2f2f2;
  margin: 0;

  &__header {
    position: unset;
    width: unset;

    .logout-link {
      margin-left: auto;
      font-size: 16px;

      &:hover {
        background-color: $gray-70;
      }
    }
  }

  &__content {
    position: relative;
    margin: 0 auto;
    padding: 32px 16px 24px;
    width: 100%;
    max-width: 1600px;
    flex-grow: 1;
    overflow: hidden;

    &::v-deep {
      .settings-grid {
        width: 100%;
        height: 100%;
        display: flex;
        position: relative;

        &__menu {
          width: 312px;
          flex-shrink: 0;
          margin-right: 48px;
          background: $lb-white;

          h1 {
            font-size: 32px;
            font-weight: $lb-medium;
            padding: 16px 16px 0;
            margin-bottom: 32px;
          }

          li:not(:last-child) {
            border-bottom: 1px solid #e8e8e8;
          }

          &__link {
            display: block;
            padding: 12px 24px;
            color: $lb-black;
            font-weight: $lb-medium;

            &:hover {
              text-decoration: none;
            }

            &.router-link-exact-active {
              background-color: $crm-blue-primary;
              color: $lb-white;
            }
          }
        }

        &__main {
          background: $lb-white;
          padding: 0 32px 32px;

          display: flex;
          flex-direction: column;
          height: auto;
          flex-grow: 1;

          &__header {
            display: flex;
            justify-content: space-between;
            position: sticky;
            top: 0;
            z-index: 10;
            padding: 32px 0 16px;
            background: $lb-white;

            &::after {
              content: '';
              width: 100%;
              height: 24px;
              position: absolute;
              top: 100%;
              left: 0;
              background: -webkit-linear-gradient(
                bottom,
                rgba(255, 255, 255, 0),
                rgba(255, 255, 255, 1)
              );
            }
          }

          h2 {
            font-size: $size-bigger;
            font-weight: $lb-medium;
          }

          .field {
            margin-bottom: 8px;

            & ~ .field {
              margin-top: 24px;
            }
          }
        }
      }
    }
  }
}
</style>

<style lang="scss">
.uppload-container {
  transition: none !important;
}
</style>
